import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import "./i18n"; // bootstraps i18next before any component renders
import { AuthProvider } from "./auth/AuthContext";
import { RequireAuth, RequirePermission } from "./auth/RequireAuth";
import { AppShell } from "./components/AppShell";
import { ToastProvider } from "./components/Toast";
import { ConfirmProvider } from "./components/ConfirmDialog";
import { PwaUpdateBanner } from "./pwa/PwaUpdateBanner";
import DashboardPage from "./pages/DashboardPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import ForbiddenPage from "./pages/ForbiddenPage";
import ProfilePage from "./pages/ProfilePage";
import AdminUsersPage from "./pages/admin/AdminUsersPage";
import AdminAuditLogPage from "./pages/admin/AdminAuditLogPage";
import AdminLocationKindsPage from "./pages/admin/AdminLocationKindsPage";
import AdminPositionKindsPage from "./pages/admin/AdminPositionKindsPage";
import AdminAttributesPage from "./pages/admin/AdminAttributesPage";
import AdminManufacturersPage from "./pages/admin/AdminManufacturersPage";
import AdminSuppliersPage from "./pages/admin/AdminSuppliersPage";
import AdminSupplierEditorPage from "./pages/admin/AdminSupplierEditorPage";
import { ATTRIBUTES_SCREEN, codebookWrite } from "./lib/codebookFunctions";
import AdminRolesPage from "./pages/admin/AdminRolesPage";
import AdminRoleEditorPage from "./pages/admin/AdminRoleEditorPage";
import AdminUserAccessPage from "./pages/admin/AdminUserAccessPage";
import LocationsPage from "./pages/LocationsPage";
import AssetTypesPage from "./pages/AssetTypesPage";
import AssetTypeEditorPage from "./pages/AssetTypeEditorPage";
import ArticleTypesPage from "./pages/ArticleTypesPage";
import ArticleTypeEditorPage from "./pages/ArticleTypeEditorPage";
import ArticlesPage from "./pages/ArticlesPage";
import ArticlePage from "./pages/ArticlePage";
import { TYPE_CATALOGS_ANY_OF } from "./components/TypeCatalogSwitch";
import StructurePage from "./pages/StructurePage";
import StructureImportPage from "./pages/StructureImportPage";
import StructureImportListPage from "./pages/StructureImportListPage";
import AssetsPage from "./pages/AssetsPage";
import AssetPage from "./pages/AssetPage";
import AssetLabelPage from "./pages/AssetLabelPage";
import AssetByTagPage from "./pages/AssetByTagPage";
import SearchPartPage from "./pages/SearchPartPage";
import "./styles.css";
import { applyTheme, getStoredTheme } from "./lib/theme";

// Heavy screens load on demand so they stay out of the main bundle (chunk > 500 kB otherwise).
const TemplatesPage = lazy(() => import("./pages/TemplatesPage"));
const TemplateEditorPage = lazy(() => import("./pages/TemplateEditorPage"));
const LayoutsPage = lazy(() => import("./pages/LayoutsPage"));
const LayoutPage = lazy(() => import("./pages/LayoutPage"));
const AssetJourneyPage = lazy(() => import("./pages/AssetJourneyPage"));

/** Lazy route boundary: a quiet placeholder, never a spinner over old content (blueprint 09 §2). */
function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<div className="card"><div className="empty">Učitavanje…</div></div>}>{children}</Suspense>;
}

/** Code lists are administered by whoever may write them (sprint 7 §5). */

/** The part search needs the type catalogue and the asset list (audit 2026-09-23 §B2). */
const PART_SEARCH = ["assets:view", "types:view"];

// The last theme this browser saw, before React paints — /auth/me corrects it if the account differs.
applyTheme(getStoredTheme());

const root = document.getElementById("root");
if (!root) throw new Error("missing #root");

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <PwaUpdateBanner />
        <ConfirmProvider>
        <Routes>
          {/* Public auth routes (no shell) */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/forbidden" element={<ForbiddenPage />} />

          {/* Authenticated, but outside the shell: QR landing + printable label */}
          <Route
            path="/a/:tag"
            element={
              <RequireAuth>
                <RequirePermission permission="assets:view">
                  <AssetByTagPage />
                </RequirePermission>
              </RequireAuth>
            }
          />
          <Route
            path="/asseti/:id/nalepnica"
            element={
              <RequireAuth>
                <RequirePermission permission="assets:view">
                  <AssetLabelPage />
                </RequirePermission>
              </RequireAuth>
            }
          />

          {/* Authenticated routes inside the AppShell */}
          <Route element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/profil" element={<ProfilePage />} />

            {/* Oprema (faza 1) */}
            <Route
              path="/struktura"
              element={
                <RequirePermission permission="structure:view">
                  <StructurePage />
                </RequirePermission>
              }
            />
            <Route
              path="/struktura/uvoz"
              element={
                <RequirePermission permission="structure:import">
                  <StructureImportListPage />
                </RequirePermission>
              }
            />
            <Route
              path="/struktura/uvoz/:id"
              element={
                <RequirePermission permission="structure:import">
                  <StructureImportPage />
                </RequirePermission>
              }
            />
            <Route
              path="/struktura/:id"
              element={
                <RequirePermission permission="structure:view">
                  <StructurePage />
                </RequirePermission>
              }
            />
            <Route
              path="/pozicija/:id"
              element={
                <RequirePermission permission="structure:view">
                  <StructurePage standalone />
                </RequirePermission>
              }
            />
            <Route
              path="/asseti"
              element={
                <RequirePermission permission="assets:view">
                  <AssetsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/asseti/:id"
              element={
                <RequirePermission permission="assets:view">
                  <AssetPage />
                </RequirePermission>
              }
            />
            <Route
              path="/asseti/:id/putanja"
              element={
                <RequirePermission permission="assets:view">
                  <Lazy><AssetJourneyPage /></Lazy>
                </RequirePermission>
              }
            />
            <Route
              path="/pretraga"
              element={
                <RequirePermission allOf={PART_SEARCH}>
                  <SearchPartPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tipovi"
              element={
                <RequirePermission anyOf={TYPE_CATALOGS_ANY_OF}>
                  <AssetTypesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tipovi/novi"
              element={
                <RequirePermission permission="types:create">
                  <AssetTypeEditorPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tipovi/:id"
              element={
                <RequirePermission permission="types:view">
                  <AssetTypeEditorPage />
                </RequirePermission>
              }
            />
            {/* Articles (sprint 5l); article types are the second view of "Tipovi delova" under /tipovi (2026-10-12) */}
            <Route
              path="/artikli"
              element={
                <RequirePermission permission="articles:view">
                  <ArticlesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/artikli/:id"
              element={
                <RequirePermission permission="articles:view">
                  <ArticlePage />
                </RequirePermission>
              }
            />
            <Route
              path="/tipovi/artikli"
              element={
                <RequirePermission permission="article-types:view">
                  <ArticleTypesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tipovi/artikli/novi"
              element={
                <RequirePermission permission="article-types:create">
                  <ArticleTypeEditorPage />
                </RequirePermission>
              }
            />
            <Route
              path="/tipovi/artikli/:id"
              element={
                <RequirePermission permission="article-types:view">
                  <ArticleTypeEditorPage />
                </RequirePermission>
              }
            />
            <Route
              path="/sabloni"
              element={
                <RequirePermission permission="templates:view">
                  <Lazy><TemplatesPage /></Lazy>
                </RequirePermission>
              }
            />
            <Route
              path="/sabloni/novi"
              element={
                <RequirePermission permission="templates:create">
                  <Lazy><TemplateEditorPage /></Lazy>
                </RequirePermission>
              }
            />
            <Route
              path="/sabloni/:id/izmena"
              element={
                <RequirePermission permission="templates:edit">
                  <Lazy><TemplateEditorPage /></Lazy>
                </RequirePermission>
              }
            />
            <Route
              path="/sabloni/:id"
              element={
                <RequirePermission permission="templates:view">
                  <Lazy><TemplatesPage /></Lazy>
                </RequirePermission>
              }
            />
            <Route
              path="/layout"
              element={
                <RequirePermission permission="layouts:view">
                  <Lazy><LayoutsPage /></Lazy>
                </RequirePermission>
              }
            />
            <Route
              path="/layout/:id"
              element={
                <RequirePermission permission="layouts:view">
                  <Lazy><LayoutPage /></Lazy>
                </RequirePermission>
              }
            />
            <Route
              path="/lokacije"
              element={
                <RequirePermission permission="locations:view">
                  <LocationsPage />
                </RequirePermission>
              }
            />

            {/* Administracija */}
            <Route
              path="/admin/korisnici"
              element={
                <RequirePermission permission="users:view">
                  <AdminUsersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/korisnici/:id/pristup"
              element={
                // Own row is readable by anyone; the page itself sends others to /forbidden (sprint 7 §1).
                <AdminUserAccessPage />
              }
            />
            <Route
              path="/admin/uloge"
              element={
                <RequirePermission permission="roles:view">
                  <AdminRolesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/uloge/nova"
              element={
                <RequirePermission permission="roles:create">
                  <AdminRoleEditorPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/uloge/:id"
              element={
                <RequirePermission permission="roles:view">
                  <AdminRoleEditorPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/audit"
              element={
                <RequirePermission permission="audit:view">
                  <AdminAuditLogPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/vrste-lokacija"
              element={
                <RequirePermission anyOf={codebookWrite("location-kinds")}>
                  <AdminLocationKindsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/vrste-pozicija"
              element={
                <RequirePermission anyOf={codebookWrite("position-kinds")}>
                  <AdminPositionKindsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/atributi"
              element={
                <RequirePermission anyOf={ATTRIBUTES_SCREEN}>
                  <AdminAttributesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/proizvodjaci"
              element={
                <RequirePermission anyOf={codebookWrite("manufacturers")}>
                  <AdminManufacturersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/dobavljaci"
              element={
                <RequirePermission anyOf={codebookWrite("suppliers")}>
                  <AdminSuppliersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/dobavljaci/novi"
              element={
                <RequirePermission permission="suppliers:create">
                  <AdminSupplierEditorPage />
                </RequirePermission>
              }
            />
            <Route
              path="/admin/dobavljaci/:id"
              element={
                <RequirePermission anyOf={codebookWrite("suppliers")}>
                  <AdminSupplierEditorPage />
                </RequirePermission>
              }
            />
          </Route>

          {/* Unmatched paths fall back to the dashboard. */}
          <Route
            path="/*"
            element={
              <RequireAuth>
                <Navigate to="/" replace />
              </RequireAuth>
            }
          />
        </Routes>
        </ConfirmProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
